import { GenericService } from 'services/genericService';
import {
	createProductOrder,
	getProducts,
	getCustomerProducts,
	createProductOrderForPostpaidLine,
	updateServices,
	submitServices,
	changeFixedPlan,
	checkResourceAvailable,
	getSimDetails,
} from 'utils/api/queries/omantelApi';
import { changeTariffPlan, checkIfResourceIsAvailable } from 'utils/api/queries/microApi';
import { Observable } from 'rxjs';
import * as payloads from 'utils/api/payloads/omantelApi';
import * as omantelMicroApi from 'utils/api/payloads/omantelMicroApi';

import { CustomerProducts } from 'utils/api/models/products/customerProducts';
import { Products } from 'utils/api/models/products/products';
import { CreateProductOrderResponse } from 'utils/api/models/products/createProductOrderResponse';
import { AccountsService } from 'services/accounts/accounts';
import { concatMap } from 'rxjs/operators';
import { getProductCharacteristic } from 'views/Numbers/helpers/getters';
import { ProductOrderResponse } from 'utils/api/models/numbers/productOrderResponse';
import { SimDetailsRequest } from 'utils/api/models/products/simDetails';
import { submitProductOrder, getProductsDetails } from 'utils/api/queries/microApi';
import { ChangeTariffPlanRequest } from 'utils/api/payloads/microApi';
import { BillingType } from 'utils/api/models/accounts/singleAccount';
import { ProductsDetails } from 'utils/api/models/postpaidLines/postpaidLines';
import { changeFbbOrder } from 'utils/api/queries/omantelMicroApi';

export class ProductsService extends GenericService {
	public static createProductOrder(productOrderInfo: payloads.ProductOrderInfo): Observable<any> {
		return createProductOrder(productOrderInfo)
			.withErrorHandling()
			.call();
	}

	public static getProducts(accountInfo: payloads.AccountInfo): Observable<Products> {
		return getProducts(accountInfo).call();
	}

	public static getCustomerProducts(accountInfo: payloads.AccountInfo): Observable<CustomerProducts> {
		return getCustomerProducts(accountInfo)
			.withErrorHandling()
			.call();
	}

	public static createProductOrderForPostpaidLine(data: payloads.NewPostpaidLineData): Observable<any> {
		return createProductOrderForPostpaidLine(data)
			.withErrorHandling()
			.call();
	}

	public static updateServices(data: payloads.ServiceData): Observable<CreateProductOrderResponse> {
		return updateServices(data)
			.disableCache()
			.call();
	}

	public static submitServices(data: payloads.SubmitServicesData): Observable<ProductOrderResponse> {
		return submitServices(data)
			.disableCache()
			.call();
	}

	public static changeFixedPlan(
		partNumber: string,
		oldPartNumber: string,
		phoneNumber: string,
		accountType: string
	): Observable<CreateProductOrderResponse> {
		return AccountsService.getCustomerAccountOperation({
			phoneNumber,
			accountType,
		}).pipe(
			concatMap((customerAccountResponse) => {
				const { customerAccount } = customerAccountResponse;
				const accountNo = getProductCharacteristic(customerAccount, 'ACCOUNT_NO');
				const phoneNo = getProductCharacteristic(customerAccount, 'PHONE_NUMBER');
				const accountExternalId = getProductCharacteristic(customerAccount, 'ACCOUNT_EXTERNAL_ID');
				const subscriberNo = getProductCharacteristic(customerAccount, 'SUBSCRIBER_NUMBER');

				return changeFixedPlan({
					partNumber: partNumber,
					phoneNumber: phoneNo,
					accountNumber: accountNo,
					accountExternalId: accountExternalId,
					subscriberNo: subscriberNo,
					oldPartNumber,
				}).call();
			})
		);
	}

	public static checkResourceAvailable(data: payloads.CheckResourceAvailable) {
		return checkResourceAvailable(data).call();
	}

	public static getSimDetails(data: SimDetailsRequest) {
		return getSimDetails(data).call();
	}

	public static submitProductOrder(data: any) {
		return submitProductOrder(data).call();
	}

	public static checkIfResourceIsAvailable(data: string) {
		return checkIfResourceIsAvailable(data).call();
	}

	public static getProductsDetails(accountId: string, billingType: BillingType): Observable<ProductsDetails> {
		return getProductsDetails(accountId, billingType).call();
	}

	public static changeTariffPlan(data: ChangeTariffPlanRequest) {
		return changeTariffPlan(data).call();
	}
	public static changeFbbOrder(data: omantelMicroApi.ChangeFbbOrder): Observable<any> {
		return changeFbbOrder(data)
			.disableCache()
			.call();
	}
}
